import React, { useCallback, useEffect, useState } from 'react';
import { Megaphone, CalendarDays, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Loading } from '@/components/OpsUI';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { NewCampaignDialog } from './NewCampaignDialog';
import { FollowupPanel } from './FollowupPanel';

function CampaignCard({ c, onOpen }) {
  const k = c.counts || {};
  const pct = k.total ? Math.round(((k.registered + k.onboarded) / k.total) * 100) : 0;
  return (
    <Card
      className="p-5 cursor-pointer hover:border-[hsl(var(--primary)/.5)] transition-colors duration-150"
      onClick={() => onOpen(c.id)}
      data-testid={`campaign-card-${c.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold truncate">{c.name}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1 mt-1">
            <CalendarDays className="h-3 w-3" /> {c.event_date || 'Sin fecha'}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 text-center">
        <div><div className="text-xl font-bold">{k.total ?? 0}</div><div className="text-[10px] text-[hsl(var(--muted-foreground))]">Congregaciones</div></div>
        <div><div className="text-xl font-bold">{k.email_sent ?? 0}</div><div className="text-[10px] text-[hsl(var(--muted-foreground))]">Correos</div></div>
        <div><div className="text-xl font-bold">{(k.registered ?? 0) + (k.onboarded ?? 0)}</div><div className="text-[10px] text-[hsl(var(--muted-foreground))]">Registradas</div></div>
      </div>
      <Progress value={pct} className="h-1.5 mt-4" />
      <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{pct}% ya registró su iglesia</div>
    </Card>
  );
}

export function CampaignList({ canAct, onOpen }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => { api.get('/campaigns').then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Megaphone className="w-6 h-6 text-[hsl(var(--primary))]" />Despliegues Ministeriales</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Congregaciones registradas en eventos: bienvenida, link de registro y seguimiento de sus primeros pasos.</p>
        </div>
        {canAct && <NewCampaignDialog onCreated={onOpen} />}
      </div>
      <Tabs defaultValue="events">
        <TabsList data-testid="campaigns-tabs">
          <TabsTrigger value="events" data-testid="campaigns-tab-events">Eventos</TabsTrigger>
          <TabsTrigger value="followup" data-testid="campaigns-tab-followup">Seguimiento WhatsApp</TabsTrigger>
        </TabsList>
        <TabsContent value="events" className="mt-4">
          {items === null ? <Loading /> : items.length === 0 ? (
            <Card className="p-10 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="campaigns-empty">
              Aún no hay eventos. Crea uno y carga el CSV de las congregaciones registradas.
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="campaigns-list">
              {items.map((c) => <CampaignCard key={c.id} c={c} onOpen={onOpen} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="followup" className="mt-4"><FollowupPanel campaigns={items || []} canAct={canAct} /></TabsContent>
      </Tabs>
    </div>
  );
}
